import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { TransactionsService } from '../../../transactions/services/transactions.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatButton } from '@angular/material/button';
import { ReactiveFormsModule } from '@angular/forms';
import { CurrencyPipe } from '@angular/common';
import { CustomerModel, PaymentMethodMode, PaymentMethodModel } from '../../../customer/customer.model';
import { ITransactionSummary, TransactionDetail } from '../../../transactions/transaction.model';
import { InvoiceDetailsInterface } from '../../payment.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap, EMPTY } from 'rxjs';
import { CardExpirationPipe } from 'shared/pipes/card-expiration.pipe';
import { CommonModule } from '@angular/common';
import { map } from 'rxjs';
import { FlashpayResponse } from '../../../transactions/transaction.model';
import {
  MODAL_SIZE,
  MODAL_TYPE,
  ModalComponentInterface,
  ModalConfig,
  ModalService } from 'shared/components/modal/modal.service';
import { FailureGuidanceModalComponent } from 'app/features/transactions/components/failure-guidance-modal/failure-guidance-modal.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TransactionReceiptComponent } from 'app/features/transactions/components/transaction-receipt/transaction-receipt.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { environment } from 'environments/environment';
import { UserStore } from 'app/features/user/store/user.store';

@Component({
  selector: 'app-collect-confirm',
  standalone: true,
  imports: [
    MatButton,
    ReactiveFormsModule,
    RouterLink,
    CurrencyPipe,
    CardExpirationPipe,
    CommonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatIconModule
  ],
  templateUrl: './collect-success.component.html',
  host: {
    class: 'page page-payment-collect-success'
  }
})
export class CollectSuccessComponent implements OnInit {
  protected Number = Number;
  customer?: CustomerModel | null;
  payment?: PaymentMethodModel | null;
  invoice?: InvoiceDetailsInterface | null;
  transaction?: TransactionDetail | null;
  protected flashpayResponse: FlashpayResponse | null = null;
  isLoading = true;
  private userStore = inject(UserStore);

  // Multi-invoice payment properties
  isMultiInvoice = false;
  multiInvoiceSuccessCount = 0;
  multiInvoiceFailCount = 0;
  /** Credit covered the payment and nothing was charged — FTE's `$0` credit application. */
  isCreditApplied = false;
  creditPaymentId: string | null = null;
  /** Paid onto the account rather than an invoice, when the payment carried it. */
  onAccountAmount = 0;
  /**
   * Declined before reaching the processor — a duplicate payment, or a repeat against an invoice
   * already paid. Nothing was charged and no transaction exists, so nothing is loaded.
   */
  isDeclinedWithoutTransaction = false;

  get isTransactionSuccess(): boolean {
    return this.transaction?.transactionFlags?.result === 'APPROVED'
      || this.transaction?.transactionResult?.resultCode === 'AUTH';
  }

  constructor(
    protected transactionService: TransactionsService,
    public router: Router,
    public route: ActivatedRoute,
    protected destroyRef: DestroyRef,
    protected modalService: ModalService,
    private snackBar: MatSnackBar
  ) {
  }

  ngOnInit(): void {
    this.isLoading = true;
    
    this.route.queryParams
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((params: { view?: string, id?: string, transactionId?: string, successCount?: string, failCount?: string, onAccount?: string }) => {
          // Handle multi-invoice view - no need to fetch transaction
          if (params.view === 'multiInvoice' || params.view === 'creditApplied') {
            this.isMultiInvoice = true;
            this.isCreditApplied = params.view === 'creditApplied';
            this.creditPaymentId = params.id ?? null;
            this.multiInvoiceSuccessCount = parseInt(params.successCount || '0');
            this.multiInvoiceFailCount = parseInt(params.failCount || '0');
            this.onAccountAmount = parseFloat(params.onAccount || '0') || 0;
            this.isLoading = false;
            return EMPTY;
          }
          
          if (params.view === 'declined') {
            this.isDeclinedWithoutTransaction = true;
            this.isLoading = false;
            return EMPTY;
          }

          if (params.view === 'flashpay' && params.id) {
            return this.transactionService.getFlashpayById(params.id).pipe(
              map(flashpay => {
                this.flashpayResponse = flashpay;
                return { flashpay };
              }),
            );
          }
          return this.transactionService.getTransaction(params.transactionId!).pipe(
          );
        })
      )
      .subscribe({
        next: (response) => {
          if (!this.isMultiInvoice && !this.isDeclinedWithoutTransaction) {
            if ('flashpay' in response) {
              //console.log('getflashpay', response.flashpay);
            } else {
              this.transaction = response;
              //console.log('transaction', response);
            }
            this.isLoading = false;
          }
        },
        error: (error) => {
          console.error('Error fetching transaction:', error);
          this.isLoading = false;
        }
      });
  }

  getCardExpirationOptions() {
    return {
      fromFormat: /^(?<month>.{2})(?<year>.{2})$/,
      toFormat: 'MM/yy'
    };
  }

  public downloadReceipt(transaction: TransactionDetail) {
    this.transactionService.getPdfReceipt(transaction);
  }
  public viewReceipt(transaction: TransactionDetail) {
    this.viewRawReceipt(transaction);
    this.transactionService.getHTMLReceipt(transaction)
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(htmlContent => {
        // Create a new window/tab and write the HTML content
        const newWindow = window.open(' ', '_blank', 'width=500,height=700,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes,titlebar=no');
        if (newWindow) {
          newWindow.document.write(htmlContent);
          newWindow.document.close();
        }
      });
  }
  public viewRawReceipt(transaction: TransactionDetail) {
    this.transactionService.getReceipt(transaction)
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(details => {
        //console.log('receipt' , details);
      });
  }

  public sendReceipt(transaction: TransactionDetail) {
    
    // Get the email from the transaction or customer data
    const customerEmail = transaction.customerDetails?.contactEmail || '';

    console.log('customerEmail', customerEmail);
    this.modalService.openModal({
      title: 'Send Receipt To Email',
      body: TransactionReceiptComponent,
      data: {
        preloadedEmail: customerEmail // Pass the email to the component
      },
      dismiss: {
        label: 'Close',
        action: (modal: ModalComponentInterface) => {
          modal.close();
        }
      },
      confirm: {
        label: 'Send',
        action: (modal: ModalComponentInterface, receiptComponent: TransactionReceiptComponent) => {
          const form = receiptComponent.form;
          if (!form.valid) {
            form.markAllAsTouched();
            return;
          }
          const formData = form.getRawValue();
          this.transactionService.sendReceipt(transaction.transactionId, true, formData.emailAddress as string).subscribe({
            next: () => {
              this.snackBar.open('Receipt sent successfully', 'Close', {
                duration: 3000
              });
            },
            error: (error) => {
              console.error('Error sending receipt to email', error);
              this.snackBar.open('Failed to send receipt', 'Close', {
                duration: 3000
              });
            }
          });
          modal.close();
        }
      }
    });
  }

  public viewFailureGuidance(transactionId: string): void {
    this.modalService.openModal({
      title: 'Failure Guidance',
      body: FailureGuidanceModalComponent,
      size: MODAL_SIZE.lg,
      data: { transactionId },
      dismiss: {
        label: 'Close',
        action: (modal: ModalComponentInterface) => modal.close()
      }
    });
  }

  get hideTax(): boolean {
    return this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.enableTax === false;
  }

  copyToClipboard(flashPayId: string | undefined) {
    if (flashPayId) {
      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      const fullUrl = `${baseUrl}/flashpay?link=${flashPayId}`;
      navigator.clipboard.writeText(fullUrl);
      this.snackBar.open('Link copied to clipboard', 'Close', {
        duration: 2000,
      });
    }
  }

}
